import { useEffect, useRef, useState } from "react";

import { useMessageBus } from "@jti/fe-message-bus";
import { Typography } from "@jti/ui/components/Typography";

import { ProductDescription } from "@product-details/components/ProductDescription";
import type { LoyaltyPriceTagState } from "@product-details/components/ProductLoyaltyBlock";
import { ProductLoyaltyBlock } from "@product-details/components/ProductLoyaltyBlock";
import { ProductQuantityError } from "@product-details/components/ProductQuantityError";
import { StockStatus } from "@product-details/components/StockStatus";
import { ConsumablesForm } from "@product-details/forms/ConsumablesForm";
import { useSyncProductWithCart } from "@product-details/hooks/useSyncProductWithCart";
import { useConsumablesProductDetailsContext } from "@product-details/providers/ConsumablesProductDetailsProvider";
import { dispatchViewItemEvent } from "@product-details/utils/dispatchViewItemEvent";

import { useConsumablesProductData } from "../../hooks/useConsumablesProductData";
import { ConsumablesProductCarousel } from "../ConsumablesProductCarousel";
import { ConsumablesProductHeading } from "../ConsumablesProductHeading";
import { ConsumablesProductPrice } from "../ConsumablesProductPrice";

import "../../../../ProductDetails.scss";

export const ConsumablesProductDetails = () => {
  const messageBus = useMessageBus();
  const { consumablesProductDetailsComponentData, currentMatrixItem } = useConsumablesProductDetailsContext();

  const { isProductDataLoading, stockStatus, priceInformation, productDisclaimer } = useConsumablesProductData();

  const { quantityLimit, isQuantityLimitReached, shouldRenderPrice, isCartLoaded } = useSyncProductWithCart({
    sku: currentMatrixItem.sku,
    priceInformation,
    hidePrice: consumablesProductDetailsComponentData.hidePrice,
    isCommerceDisabled: consumablesProductDetailsComponentData.isCommerceDisabled,
  });

  const productDescription = currentMatrixItem.product.description || "";
  const [loyaltyPriceTagState, setLoyaltyPriceTagState] = useState<LoyaltyPriceTagState>({ status: "hidden" });

  useEffect(() => {
    const productName = currentMatrixItem.product.name;
    messageBus.publish("update.breadcrumb.name", {
      payload: {
        breadcrumbName: productName,
      },
    });
    window.document.title = productName;
  }, [currentMatrixItem]);

  const flavor = currentMatrixItem?.product?.flavor;
  const lastTrackedFlavorRef = useRef<string | null>(null);

  useEffect(() => {
    if (isProductDataLoading || !currentMatrixItem?.product || lastTrackedFlavorRef.current === flavor) {
      return;
    }

    dispatchViewItemEvent({
      productName: currentMatrixItem.product.name,
      sku: currentMatrixItem.sku,
      color: currentMatrixItem.product.color ?? null,
      flavor,
      categories: currentMatrixItem.product.categories ?? null,
      discount: currentMatrixItem.product?.tierPrices?.[0]?.discount?.percentOff ?? 0,
      priceInformation: currentMatrixItem.product?.tierPrices?.[0] ?? null,
    });
    lastTrackedFlavorRef.current = flavor;
  }, [flavor, isProductDataLoading, currentMatrixItem?.product]);

  return (
    <div className="productDetails__container" data-testid="sticks-product-details" data-product-type="consumables">
      <div className="carouselapper carouselttomSpacing productDetails__carousel">
        <ConsumablesProductCarousel />
      </div>

      <div className="productDetails__productInfo">
        <ConsumablesProductHeading />

        {shouldRenderPrice && (
          <ConsumablesProductPrice
            loyaltyPriceTagState={loyaltyPriceTagState}
            loyaltyBadgeCopy={consumablesProductDetailsComponentData.loyaltyBadgeCopy}
          />
        )}

        {!!productDisclaimer && (
          <Typography
            variant="body1"
            className="product-details__disclaimer"
            data-testid="consumables-product-disclaimer"
          >
            {productDisclaimer}
          </Typography>
        )}

        {isQuantityLimitReached && (
          <ProductQuantityError className="product-details__quantity-error" isVisible quantityLimit={quantityLimit} />
        )}

        <div className="product-details__sticks-form-wrapper">
          <ConsumablesForm
            isSubscriptionsEnabled={consumablesProductDetailsComponentData.isSubscriptionsEnabled}
            isCartLoaded={isCartLoaded}
            customBuyCtaLabel={consumablesProductDetailsComponentData.customBuyCtaLabel}
            isPriceDropdownEnabled={consumablesProductDetailsComponentData.isPriceDropdownEnabled}
            priceDropdownPromotionalMessage={consumablesProductDetailsComponentData.priceDropdownPromotionalMessage}
            priceDropdownPromotionalTooltip={consumablesProductDetailsComponentData.priceDropdownPromotionalTooltip}
          />
        </div>

        <div className={"product-information-block"}>
          <StockStatus stockStatus={stockStatus} isProductDataLoading={isProductDataLoading} />
          <ProductDescription description={productDescription} />
          <ProductLoyaltyBlock
            knowMoreUrl={consumablesProductDetailsComponentData.knowMoreUrl}
            sku={currentMatrixItem.sku}
            unitPrice={priceInformation?.finalPrice?.value ?? null}
            currency={priceInformation?.finalPrice?.currency}
            loyaltyRewardCopy={consumablesProductDetailsComponentData.loyaltyRewardCopy}
            isLoyaltyRewardCopyAuthoringEnabled={
              consumablesProductDetailsComponentData.isLoyaltyRewardCopyAuthoringEnabled ?? false
            }
            onPriceTagStateChange={setLoyaltyPriceTagState}
          />
        </div>
      </div>
    </div>
  );
};
